<template>
  <div
    class="panorama"
    ref="wrapper"
    @mousedown.capture="panoramaClickHandler"
    @mouseleave="mouseleaveHandler"
    @touchmove.capture="panoramaClickHandler"
  >
    <panorama-compass
      v-if="$refs.panorama && $refs.panorama"
      :viewer="$refs.panorama.viewer"
      class="panorama__compass"
    />
    <pano-viewer
      ref="panorama"
      :main-scene="$panorama.structure.flyView.index"
      :camera-fov="$panorama.options.fov.start"
      :min-fov="$panorama.options.fov.min"
      :max-fov="$panorama.options.fov.max"
      :control-bar="false"
      @loadScene="loadSceneHandler"
      @progressScene="runLoading"
      @mousedown.native="setCursor('grabbing')"
      @mouseup.native="setCursor('auto')"
    >
      <pano-scene
        :name="$panorama.structure.flyView.index"
        :source="$panorama.structure.flyView.image"
        :center="$panorama.structure.flyView.center"
        :min-polar-angle="$panorama.structure.flyView.polarAngle.min"
        :max-polar-angle="$panorama.structure.flyView.polarAngle.max"
        :segments="70"
        :radius="500"
      >
        <panorama-spot
          v-for="(infoSpot, index) in spots"
          :key="`fly-${index}-${infoSpot.label}`"
          :position="infoSpot.position"
          :label="infoSpot.label"
          :icon="infoSpot.icon"
          :type="infoSpot.type"
          :background-color="infoSpot.backgroundColor"
        />
      </pano-scene>
    </pano-viewer>
    <div class="panorama__zoom-control zoom-control">
      <span class="zoom-control__item button" @click="runZoom(-10)"> + </span>
      <span class="zoom-control__item button" @click="runZoom(10)"> - </span>
    </div>
    <div v-if="loading" class="panorama-loader">Loading...</div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import PanoramaSpot from '@/components/elements/PanoramaSpot'
import PanoramaCompass from '@/components/elements/PanoramaCompass'

export default {
  name: 'Panorama',
  components: {
    PanoramaSpot,
    PanoramaCompass
  },
  data: () => ({
    showAllLabels: true,
    spots: [],
    // Началось ли взаимодействие после перехода на сцену
    beginInteraction: false
  }),
  computed: {
    ...mapState('Panorama', ['loading']),
    ...mapState('Panorama/Control', ['activeCategory']),
    panorama () {
      return this.$refs.panorama
    },
    viewer () {
      return this.panorama.viewer
    },
    isShowInfoSpot () {
      return infoSpot =>
        (infoSpot.isShow !== undefined ? infoSpot.isShow : true) &&
          (!infoSpot.category ||
        infoSpot.category.toLowerCase().includes(this.$panorama.control.categories[this.activeCategory].toLowerCase()))
    }
  },
  methods: {
    ...mapMutations({
      runLoading: 'Panorama/RUN_LOADING',
      stopLoading: 'Panorama/STOP_LOADING'
    }),
    loadSceneHandler () {
      this.stopLoading()
    },
    panoramaClickHandler () {
      this.beginInteraction = true
    },
    panoramaResizeHandler () {
      // Хак чтобы выполнить адаптацию панорамы чуть позже после события
      setTimeout(this.viewer.HANDLER_WINDOW_RESIZE, 0)
    },
    mouseleaveHandler () {
      // Если увели курсор с панорамы вызываем событие mouseup на дом элементе
      // чтобы после возвращения при драге панорама не двигалась
      this.$panorama.utils.eventFire(this.panorama.$el, 'mouseup')
    },
    async runZoom (value) {
      const k = value / Math.abs(value)
      for (let i = 1; i <= Math.abs(value); i++) {
        const { min, max } = this.$panorama.options.fov
        const fov = this.viewer.camera.fov
        if (fov + k < min || fov + k > max) return
        this.viewer.setCameraFov(fov + k)
        await this.$panorama.utils.delay(20)
      }
    },
    setCursor (type) {
      this.viewer.renderer.domElement.style.cursor = type
    }
  },
  mounted () {
    this.viewer.getControl().momentumDampingFactor = 0.5
    this.viewer.getControl().update()
    this.$nextTick(this.panoramaResizeHandler)
    window.addEventListener('resize', this.panoramaResizeHandler)
  },
  beforeMount () {
  },
  beforeCreate () {
    if (this.$panorama.utils.isMobile) {
      this.$panorama.options.fov.min = this.$panorama.options.fov._minMobile
      this.$panorama.options.fov.max = this.$panorama.options.fov._maxMobile
    }
  },
  created () {
    this.spots = this.$panorama.structure.flyView.endPoints
  },
  destroyed () {
    window.removeEventListener('resize', this.panoramaResizeHandler)
  }
}
</script>

<style lang="scss" scoped>
@import '~@black40x/vue-pano/dist/vue-pano.css';
@import '~swiper/css/swiper.css';
.panorama {
  position: relative;
  overflow: hidden;
  &__call-hints {
    position: absolute;
    right: 30px;
    bottom: 30px;
    @media screen and (min-width: 1920px) {
      right: 1.6vw;
      bottom: 1.6vw;
      & > svg {
        width: 1.65vw;
        height: 1.65vw;
      }
    }
  }
  &__control {
    position: absolute;
    top: 10px;
    left: 10px;
    @media screen and (min-width: 768px) {
      top: 15px;
      left: 32px;
    }
    @media screen and (min-width: 1920px) {
      top: 0.85vw;
      left: 1.65vw;
    }
  }
  &__compass {
    position: absolute;
    top: 10px;
    right: 10px;
    @media screen and (min-width: 768px) {
      top: 15px;
      right: 32px;
    }
    @media screen and (min-width: 1920px) {
      top: 0.85vw;
      right: 1.65vw;
    }
  }
  &__zoom-control {
    position: absolute;
    right: 0;
    top: 50%;
  }
  &, & * {
    box-sizing: border-box;
  }
}

::v-deep {
  .button {
    &, &:focus, &:active {
      background: transparent;
      border: none;
      outline: none;
    }
    &:hover {
      cursor: pointer;
      opacity: 0.7;
    }
    &:disabled {
      cursor: default;
      opacity: 0.7;
    }
  }
}

.zoom-control {
  display: flex;
  flex-direction: column;
  &__item {
    font-size: 16px;
    padding: 10px 15px;
    font-family: var(--panorama-second-font);
    background-color: var(--panorama-background-color);
    color: var(--panorama-text-color);
    &:hover, &:focus {
      background-color: var(--panorama-background-color);
    }
    &:hover {
      opacity: 0.8;
    }
    &:not(:last-child) {
      border-bottom: 1px solid var(--panorama-text-color);
    }
    @media screen and (min-width: 1920px) {
      font-size: 0.7vw;
      padding: 0.5vw 0.8vw;
    }
  }
}

.panorama-loader {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  font-family: var(--panorama-second-font);
  background-color: var(--panorama-text-color);
  opacity: .5;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #FFF;
  font-size: 14px;
  @media screen and (min-width: 1920px) {
    font-size: 0.75vw;
  }
}
</style>
